import {
  ApInput,
  ApMenu,
  ApMenuItem,
  ApSearchDropdown,
} from '@alixpartners/ui-components';
import React from 'react';

import {
  AllocationContainer,
  Container,
  Equal,
  ErrorMessage,
  SelectContainer,
  RowMenuIcon,
  WarningMessage,
} from './IndustryRow.styles';

type IOption = {
  name: string;
  value: string;
};

const NO_INDUSTRY_DATA_MESSAGE =
  'No benchmark data available for any year. Please choose another industry.';
const NO_MATCHING_INDUSTRY_MESSAGE = 'No matching industry found';

const getDropdownLabel = ({
  options,
  value,
}: {
  value?: string;
  options: IOption[];
}) => {
  const found = options.find((option) => option.value === value);
  return found ? found.name : '';
};

export const IndustryRow = ({
  apIndustryCode,
  gartnerIndustryCode,
  computerEconomicsIndustryCode,
  allocationPercentage,
  id,
  apIndustriesOptions,
  computerEconomicsIndustriesOptions,
  gartnerIndustriesOptions,
  isCorrectAllocation,
  apIndustryNoMatchingOptions,
  gartnerIndustryNoMatchingOptions,
  computerEconomicsIndustryNoMatchingOptions,
  missingDataProviders,
  missingProviders,
  onChange,
  onRemove,
  onReset,
}: {
  allocationPercentage: number;
  apIndustriesOptions: IOption[];
  apIndustryCode?: string;
  apIndustryNoMatchingOptions?: boolean;
  computerEconomicsIndustriesOptions: IOption[];
  computerEconomicsIndustryCode?: string;
  computerEconomicsIndustryNoMatchingOptions?: boolean;
  gartnerIndustriesOptions: IOption[];
  gartnerIndustryCode?: string;
  gartnerIndustryNoMatchingOptions?: boolean;
  id: number;
  isCorrectAllocation: boolean;
  missingDataProviders: any[];
  missingProviders: any[];
  onChange: (id: number, o: any) => void;
  onRemove: (id: number) => void;
  onReset: (id: number) => void;
}) => {
  const gartnerIndustryError = missingProviders.find(
    (provider) => provider.industryCode === gartnerIndustryCode
  );
  const computerEconomicsIndustryError = missingProviders.find(
    (provider) => provider.industryCode === computerEconomicsIndustryCode
  );
  const gartnerMissingDataForYear = missingDataProviders.find(
    (provider) => provider.l1Code === gartnerIndustryCode
  );
  const computerEconomicsMissingDataForYear = missingDataProviders.find(
    (provider) => provider.l1Code === computerEconomicsIndustryCode
  );
  const isIndustryError =
    gartnerIndustryError || computerEconomicsIndustryError;
  const isIndustryWarning =
    gartnerMissingDataForYear || computerEconomicsMissingDataForYear;
  const isNoMatchingMessage =
    apIndustryNoMatchingOptions ||
    gartnerIndustryNoMatchingOptions ||
    computerEconomicsIndustryNoMatchingOptions;
  const isAnyErrorMessage =
    isIndustryWarning || isIndustryError || isNoMatchingMessage;
  return (
    <Container>
      <SelectContainer>
        <ApSearchDropdown
          options={apIndustriesOptions}
          value={getDropdownLabel({
            value: apIndustryCode,
            options: apIndustriesOptions,
          })}
          disabled={apIndustriesOptions.length === 0}
          placeholder="Select or type"
          onSuggestionSelected={(apIndustryCode) => {
            onChange(id, {
              apIndustryCode,
              gartnerIndustryCode: '',
              computerEconomicsIndustryCode: '',
            });
          }}
        />
        {isAnyErrorMessage ? (
          <ErrorMessage visible={apIndustryNoMatchingOptions}>
            {NO_INDUSTRY_DATA_MESSAGE}
            {apIndustryNoMatchingOptions && NO_MATCHING_INDUSTRY_MESSAGE}
          </ErrorMessage>
        ) : null}
      </SelectContainer>

      <AllocationContainer highlight={!isCorrectAllocation}>
        <ApInput
          name="allocation-select"
          value={String(allocationPercentage)}
          onChange={(value) => {
            onChange(id, {
              allocationPercentage: Number(value),
            });
          }}
          symbol="%"
        />
        {isAnyErrorMessage ? <ErrorMessage /> : null}
      </AllocationContainer>

      <Equal>=</Equal>

      <SelectContainer>
        <ApSearchDropdown
          options={gartnerIndustriesOptions}
          value={getDropdownLabel({
            value: gartnerIndustryCode,
            options: gartnerIndustriesOptions,
          })}
          disabled={gartnerIndustriesOptions.length === 0}
          placeholder="Select or type"
          onSuggestionSelected={(gartnerIndustryCode) => {
            onChange(id, { gartnerIndustryCode });
          }}
        />
        {isAnyErrorMessage && !gartnerMissingDataForYear ? (
          <ErrorMessage
            visible={gartnerIndustryError || gartnerIndustryNoMatchingOptions}
          >
            {gartnerIndustryError && NO_INDUSTRY_DATA_MESSAGE}
            {gartnerIndustryNoMatchingOptions && NO_MATCHING_INDUSTRY_MESSAGE}
          </ErrorMessage>
        ) : null}
        {gartnerMissingDataForYear ? (
          <WarningMessage>
            Latest benchmark data available for {gartnerMissingDataForYear.year}{' '}
            year
          </WarningMessage>
        ) : null}
      </SelectContainer>

      <SelectContainer noMargin>
        <ApSearchDropdown
          options={computerEconomicsIndustriesOptions}
          value={getDropdownLabel({
            value: computerEconomicsIndustryCode,
            options: computerEconomicsIndustriesOptions,
          })}
          disabled={computerEconomicsIndustriesOptions.length === 0}
          placeholder="Select or type"
          onSuggestionSelected={(computerEconomicsIndustryCode) => {
            onChange(id, { computerEconomicsIndustryCode });
          }}
        />
        {isAnyErrorMessage && !computerEconomicsMissingDataForYear ? (
          <ErrorMessage
            visible={
              computerEconomicsIndustryError ||
              computerEconomicsIndustryNoMatchingOptions
            }
          >
            {computerEconomicsIndustryError && NO_INDUSTRY_DATA_MESSAGE}
            {computerEconomicsIndustryNoMatchingOptions &&
              NO_MATCHING_INDUSTRY_MESSAGE}
          </ErrorMessage>
        ) : null}
        {computerEconomicsMissingDataForYear ? (
          <WarningMessage>
            Latest benchmark data available for{' '}
            {computerEconomicsMissingDataForYear.year} year
          </WarningMessage>
        ) : null}
      </SelectContainer>
      <ApMenu button={<RowMenuIcon iconName="more_vert" />}>
        <ApMenuItem onClick={() => onReset(id)}>Reset</ApMenuItem>
        <ApMenuItem onClick={() => onRemove(id)}>Delete</ApMenuItem>
      </ApMenu>
    </Container>
  );
};
